import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError, delay } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  MOCK_PROVEEDORES,
  MOCK_CUENTAS,
  MOCK_MEDIOS_PAGO,
  MOCK_FACTURAS_PENDIENTES,
  MOCK_CHEQUES_CARTERA,
  MOCK_CUENTAS_BANCARIAS,
  MOCK_TIPOS_GASTO,
  MOCK_PAGOS,
  getNextPagoId,
  getNextNumero,
  agregarPagoMock,
  eliminarTipoGastoMock,
  crearTipoGastoMock,
  response,
  paginatedResponse,
} from './mock-data/pagos-proveedores.mock';

// ══════════════════ Configuración de errores ══════════════════
// Cambiá estos flags para simular distintos escenarios de error.
const SIMULAR_ERROR_CREAR_PAGO = false;   // Si true, POST /pagos-proveedores devuelve 422
const SIMULAR_ERROR_ANULAR = false;       // Si true, anular pago devuelve 500
const DELAY_MS = 350;                     // Latencia artificial (ms)

// ══════════════════ Helper ══════════════════

function ok<T>(data: T): Observable<HttpResponse<T>> {
  return of(new HttpResponse({ status: 200, body: data })).pipe(delay(DELAY_MS));
}

function err(status: number, errorBody: any): Observable<never> {
  return throwError(() => new HttpErrorResponse({
    status,
    error: errorBody
  })).pipe(delay(DELAY_MS));
}

/** Extrae el ID numérico de una URL tipo /algo/123 */
function extractId(url: string, prefix: string): number | null {
  const regex = new RegExp(`${prefix}/(\\d+)`);
  const match = url.match(regex);
  return match ? parseInt(match[1], 10) : null;
}

// ══════════════════ Interceptor ══════════════════

export const mockInterceptor: HttpInterceptorFn = (req, next) => {
  if (!environment.mockMode) {
    return next(req);
  }

  const url = req.url;
  const method = req.method;

  // ── GET /tenant/pagos-proveedores/{id} ──
  if (method === 'GET' && url.match(/\/pagos-proveedores\/\d+$/)) {
    const id = extractId(url, 'pagos-proveedores');
    const pago = MOCK_PAGOS.find(p => p.id === id);
    if (!pago) return err(404, 'Pago no encontrado');
    return ok(response(pago));
  }

  // ── GET /tenant/pagos-proveedores (listado) ──
  if (method === 'GET' && url.includes('/pagos-proveedores') && !url.includes('facturas-pendientes') && !url.includes('proveedores-con-saldo')) {
    let pagos = [...MOCK_PAGOS];

    // Filtro por search
    const searchMatch = url.match(/[?&]search=([^&]*)/);
    if (searchMatch && searchMatch[1]) {
      const s = decodeURIComponent(searchMatch[1]).toLowerCase();
      pagos = pagos.filter(p =>
        p.numero.toLowerCase().includes(s) ||
        (p.proveedor as any)?.razon_social?.toLowerCase().includes(s)
      );
    }

    // Filtro por estado
    const estadoMatch = url.match(/[?&]estado=([^&]*)/);
    if (estadoMatch && estadoMatch[1]) {
      pagos = pagos.filter(p => p.estado === estadoMatch[1]);
    }

    const perPage = parseInt(url.match(/[?&]per_page=(\d+)/)?.[1] || '15', 10);
    return ok(paginatedResponse(pagos, perPage));
  }

  // ── POST /tenant/pagos-proveedores (crear) ──
  if (method === 'POST' && url.match(/\/pagos-proveedores$/) && !url.includes('anular')) {
    if (SIMULAR_ERROR_CREAR_PAGO) {
      return err(422, {
        message: 'The given data was invalid.',
        errors: { total: ['El total no puede ser cero.'] }
      } as any);
    }

    const body = req.body as any;
    const id = getNextPagoId();
    const numero = getNextNumero();

    const medios = (body.medios || []).map((m: any, idx: number) => ({
      id: idx + 1000 + id,
      medio_pago_id: m.medio_pago_id,
      cuenta_fondo_id: m.cuenta_fondo_id || null,
      nota_credito_id: m.nota_credito_id || null,
      importe: m.importe,
      referencia: m.referencia || null,
      medio_pago: MOCK_MEDIOS_PAGO.find(mp => mp.id === m.medio_pago_id) || { id: m.medio_pago_id, nombre: 'Desconocido' },
    }));

    const imputaciones = (body.imputaciones || []).map((imp: any, idx: number) => ({
      id: idx + 1000 + id,
      compra_id: imp.compra_id,
      importe: imp.importe,
      compra: { id: imp.compra_id, punto_venta_numero: `0001-00000${imp.compra_id}`, fecha: '2026-07-01', total: imp.importe * 1.5, tipo_comprobante: 'factura_a', tipo_gasto: null },
    }));

    const total = (body.medios || []).reduce((s: number, m: any) => s + (Number(m.importe) || 0), 0);

    const proveedor = MOCK_PROVEEDORES.find(p => p.id === body.proveedor_id);
    const saldoProveedor = 50000 - total;

    const pago: any = {
      id,
      proveedor_id: body.proveedor_id,
      numero,
      fecha: body.fecha || new Date().toISOString().split('T')[0],
      total,
      estado: 'confirmada',
      fecha_anulacion: null,
      motivo_anulacion: null,
      observaciones: body.observaciones || null,
      proveedor: proveedor ? { id: proveedor.id, codigo: proveedor.codigo, razon_social: proveedor.razon_social } : null,
      usuario: { id: 1, nombre: 'Admin (mock)', apellido: '' },
      medios,
      imputaciones,
    };

    agregarPagoMock(pago);
    return ok({ success: true, data: { pago, saldo_proveedor: saldoProveedor }, message: 'Pago registrado' });
  }

  // ── POST /tenant/pagos-proveedores/{id}/anular ──
  if (method === 'POST' && url.includes('/anular')) {
    if (SIMULAR_ERROR_ANULAR) {
      return err(500, 'Error interno del servidor');
    }
    const id = extractId(url, 'pagos-proveedores');
    const pago = MOCK_PAGOS.find(p => p.id === id);
    if (pago) {
      pago.estado = 'anulada';
    }
    return ok({
      success: true,
      data: { pago: pago!, saldo_proveedor: 100000 },
      message: 'Pago anulado'
    });
  }

  // ── GET /tenant/proveedores/{id}/cuenta ──
  if (method === 'GET' && url.match(/\/proveedores\/\d+\/cuenta/)) {
    const id = extractId(url, 'proveedores');
    const proveedor = MOCK_PROVEEDORES.find(p => p.id === id);
    const saldo = id === 1 ? 45000 : id === 2 ? 78000 : 65000;
    return ok(response({
      proveedor: proveedor || { id, razon_social: 'Desconocido' },
      saldo,
      movimientos: { data: [], current_page: 1, total: 0, per_page: 15 },
    }));
  }

  // ── GET /tenant/pagos-proveedores/facturas-pendientes/{id} ──
  if (method === 'GET' && url.includes('/facturas-pendientes/')) {
    const id = extractId(url, 'facturas-pendientes');
    const facturas = MOCK_FACTURAS_PENDIENTES[id!] || [];
    return ok(response(facturas));
  }

  // ── GET /tenant/pagos-proveedores/proveedores-con-saldo ──
  if (method === 'GET' && url.includes('/proveedores-con-saldo')) {
    const data = MOCK_PROVEEDORES.map(p => ({
      id: p.id,
      codigo: p.codigo,
      razon_social: p.razon_social,
      cuit: p.cuit,
      saldo: p.id === 1 ? 45000 : p.id === 2 ? 78000 : 65000,
    }));
    return ok(response(data));
  }

  // ── GET /tenant/medios-pago ──
  if (method === 'GET' && url.includes('/medios-pago')) {
    return ok(response(MOCK_MEDIOS_PAGO));
  }

  // ── GET /tenant/cuentas-fondo ──
  if (method === 'GET' && url.includes('/cuentas-fondo')) {
    return ok(response(MOCK_CUENTAS));
  }

  // ── GET /tenant/cheques ──
  if (method === 'GET' && url.includes('/cheques')) {
    let cheques = [...MOCK_CHEQUES_CARTERA];

    const estadoMatch = url.match(/[?&]estado=([^&]*)/);
    if (estadoMatch && estadoMatch[1]) {
      cheques = cheques.filter(c => c.estado === estadoMatch[1]);
    }

    const tipoMatch = url.match(/[?&]tipo=([^&]*)/);
    if (tipoMatch && tipoMatch[1]) {
      cheques = cheques.filter(c => c.tipo === tipoMatch[1]);
    }

    const perPage = parseInt(url.match(/[?&]per_page=(\d+)/)?.[1] || '15', 10);
    return ok({ success: true, data: paginatedResponse(cheques, perPage), message: null });
  }

  // ── GET /tenant/cuentas-bancarias ──
  if (method === 'GET' && url.includes('/cuentas-bancarias')) {
    return ok(response(MOCK_CUENTAS_BANCARIAS));
  }

  // ── GET /tenant/tipogastos ──
  if (method === 'GET' && url.includes('/tipogastos')) {
    return ok(response(MOCK_TIPOS_GASTO));
  }

  // ── POST /tenant/tipogastos (crear) ──
  if (method === 'POST' && url.includes('/tipogastos') && !url.includes('anular')) {
    const body = req.body as any;
    const nuevo = crearTipoGastoMock(body.nombre);
    return ok(response(nuevo));
  }

  // ── DELETE /tenant/tipogastos/{id} ──
  if (method === 'DELETE' && url.includes('/tipogastos/')) {
    const id = extractId(url, 'tipogastos');
    if (id) eliminarTipoGastoMock(id);
    return ok(response({ success: true }));
  }

  // ── PUT /tenant/compras/{id}/tipogasto ──
  if (method === 'PUT' && url.includes('/tipogasto')) {
    return ok(response({ success: true }));
  }

  // ── GET /tenant/proveedores (listado general) ──
  if (method === 'GET' && url.includes('/proveedores') && !url.includes('/cuenta') && !url.includes('con-saldo')) {
    const activoMatch = url.match(/[?&]activo=([^&]*)/);
    let data = [...MOCK_PROVEEDORES];
    if (activoMatch && activoMatch[1] === 'true') {
      data = data.filter(p => p.activo);
    }
    return ok(response(data));
  }

  // ── Cualquier otra ruta: pasar al backend real ──
  console.warn(`%c[MOCK] Ruta no mockeada: ${method} ${url}`, 'color: orange; font-weight: bold');
  return next(req);
};
